import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { Button, Tooltip } from 'antd';
import notification from '../../../commonComponents/Notification/Notification';
import { ArrowLeftOutlined, MailOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import { EmailTemplateApi } from '../services/EmailTemplateApi';
import { DEFAULT_TEMPLATE_TYPES, getTemplateTypesWithFallback } from '../constants/emailConstants';
import StatsCards from '../StatsCards/StatsCards';
import TemplateFilters from '../TemplateFilters/TemplateFilters';
import TemplateTable from '../TemplateTable';
import TemplateFormModal from '../TemplateFormModal';
import TestEmailModal from '../TestEmailModal';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import { TEMPLATE_LIST_DEFAULTS, TOAST_MESSAGES, FALLBACK_STATS } from './constants';
import { EmailTemplate, StatsData, TemplateType } from './types';
import * as S from './styled';

const EmailTemplateList: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [templateTypes, setTemplateTypes] = useState<TemplateType[]>([]);
  const [currentPage, setCurrentPage] = useState(
    TEMPLATE_LIST_DEFAULTS.INITIAL_PAGE
  );
  const [limit] = useState(TEMPLATE_LIST_DEFAULTS.INITIAL_LIMIT);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isDefaultFilter, setIsDefaultFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isTestOpen, setIsTestOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(
    null
  );
  const [templateToDelete, setTemplateToDelete] = useState<EmailTemplate | null>(
    null
  );
  const [formLoading, setFormLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        limit,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
        ...(isDefaultFilter !== '' && {
          isDefault: isDefaultFilter === 'true',
        }),
      };

      const response = (await EmailTemplateApi.getAllTemplates(params)) as any;
      setTemplates(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      notification.error(TOAST_MESSAGES.FETCH_ERROR.title, TOAST_MESSAGES.FETCH_ERROR.description);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchTerm, statusFilter, typeFilter, isDefaultFilter]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = (await EmailTemplateApi.getTemplateStats()) as any;
      setStats(response.data?.data || FALLBACK_STATS);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(FALLBACK_STATS);
      notification.warning(TOAST_MESSAGES.STATS_ERROR.title, TOAST_MESSAGES.STATS_ERROR.description);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchTemplateTypes = useCallback(async () => {
    try {
      const response = (await EmailTemplateApi.getTemplateTypes()) as any;
      setTemplateTypes(response.data?.data || DEFAULT_TEMPLATE_TYPES);
    } catch (error) {
      console.error('Error fetching template types:', error);
      setTemplateTypes(DEFAULT_TEMPLATE_TYPES);
      notification.warning(TOAST_MESSAGES.TYPES_ERROR.title, TOAST_MESSAGES.TYPES_ERROR.description);
    }
  }, []);

  const getTemplateVariables = useCallback(
    async (type: string) => {
      try {
        const response = (await EmailTemplateApi.getTemplateVariables(
          type
        )) as any;
        return response.data || [];
      } catch (error) {
        console.error('Error fetching template variables:', error);
        return [];
      }
    },
    []
  );

  const handleSaveTemplate = useCallback(
    async (templateData: Partial<EmailTemplate>) => {
      try {
        setFormLoading(true);

        if (selectedTemplate) {
          const updatedTemplate = { ...selectedTemplate, ...templateData };
          setTemplates((prev) =>
            prev.map((t) =>
              t.id === selectedTemplate.id ? (updatedTemplate as EmailTemplate) : t
            )
          );

          await EmailTemplateApi.updateTemplate(
            selectedTemplate.id,
            templateData
          );
          notification.success(TOAST_MESSAGES.SAVE_SUCCESS.title, TOAST_MESSAGES.SAVE_SUCCESS.description);
        } else {
          const tempId = `temp_${Date.now()}`;
          const newTemplate = {
            ...templateData,
            id: tempId,
            createdAt: new Date().toISOString(),
          } as EmailTemplate;
          setTemplates((prev) => [newTemplate, ...prev]);

          const response = (await EmailTemplateApi.createTemplate(
            templateData
          )) as any;

          setTemplates((prev) =>
            prev.map((t) =>
              t.id === tempId ? response.data?.data : t
            )
          );
          notification.success(TOAST_MESSAGES.SAVE_SUCCESS.title, TOAST_MESSAGES.SAVE_SUCCESS.description);
        }

        setIsFormOpen(false);
        setSelectedTemplate(null);
        await Promise.all([fetchTemplates(), fetchStats()]);
      } catch (error) {
        console.error('Error saving template:', error);
        notification.error(
          TOAST_MESSAGES.SAVE_ERROR.title,
          (error as any)?.response?.data?.message ||
            TOAST_MESSAGES.SAVE_ERROR.description
        );
        fetchTemplates();
      } finally {
        setFormLoading(false);
      }
    },
    [selectedTemplate, fetchTemplates, fetchStats]
  );

  const handleDeleteTemplate = useCallback(async () => {
    if (!templateToDelete) return;

    try {
      setDeleteLoading(true);
      const templateId = templateToDelete.id;
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));

      await EmailTemplateApi.deleteTemplate(templateId);
      notification.success(TOAST_MESSAGES.DELETE_SUCCESS.title, TOAST_MESSAGES.DELETE_SUCCESS.description);

      setIsDeleteOpen(false);
      setTemplateToDelete(null);
      await Promise.all([fetchTemplates(), fetchStats()]);
    } catch (error) {
      console.error('Error deleting template:', error);
      notification.error(
        TOAST_MESSAGES.DELETE_ERROR.title,
        (error as any)?.response?.data?.message ||
          TOAST_MESSAGES.DELETE_ERROR.description
      );
      fetchTemplates();
    } finally {
      setDeleteLoading(false);
    }
  }, [templateToDelete, fetchTemplates, fetchStats]);

  const handleDuplicateTemplate = useCallback(
    async (template: EmailTemplate) => {
      try {
        const newName = `${template.name} (Copy)`;
        const tempId = `temp_${Date.now()}`;
        const duplicatedTemplate = {
          ...template,
          id: tempId,
          name: newName,
          createdAt: new Date().toISOString(),
          usageCount: 0,
          isDefault: false,
        };
        setTemplates((prev) => [duplicatedTemplate, ...prev]);

        const response = (await EmailTemplateApi.duplicateTemplate(
          template.id,
          newName
        )) as any;

        setTemplates((prev) =>
          prev.map((t) => (t.id === tempId ? response.data?.data : t))
        );
        notification.success(TOAST_MESSAGES.DUPLICATE_SUCCESS.title, TOAST_MESSAGES.DUPLICATE_SUCCESS.description);
        await Promise.all([fetchTemplates(), fetchStats()]);
      } catch (error) {
        console.error('Error duplicating template:', error);
        notification.error(
          TOAST_MESSAGES.DUPLICATE_ERROR.title,
          (error as any)?.response?.data?.message ||
            TOAST_MESSAGES.DUPLICATE_ERROR.description
        );
        setTemplates((prev) =>
          prev.filter((t) => !t.id.toString().startsWith('temp_'))
        );
      }
    },
    [fetchTemplates, fetchStats]
  );

  const handleSetDefault = useCallback(
    async (template: EmailTemplate) => {
      try {
        setTemplates((prev) =>
          prev.map((t) => ({
            ...t,
            isDefault: t.id === template.id,
          }))
        );

        await EmailTemplateApi.setAsDefault(template.id);
        notification.success(TOAST_MESSAGES.SET_DEFAULT_SUCCESS.title, TOAST_MESSAGES.SET_DEFAULT_SUCCESS.description);
        await fetchTemplates();
      } catch (error) {
        console.error('Error setting default:', error);
        notification.error(
          TOAST_MESSAGES.SET_DEFAULT_ERROR.title,
          (error as any)?.response?.data?.message ||
            TOAST_MESSAGES.SET_DEFAULT_ERROR.description
        );
        fetchTemplates();
      }
    },
    [fetchTemplates]
  );

  const handleSendTestEmail = useCallback(
    async (testData: any) => {
      if (!selectedTemplate) return;

      try {
        setTestLoading(true);
        await EmailTemplateApi.sendTestEmail(selectedTemplate.id, testData);
        notification.success(TOAST_MESSAGES.TEST_EMAIL_SUCCESS.title, TOAST_MESSAGES.TEST_EMAIL_SUCCESS.description);
        setIsTestOpen(false);
      } catch (error) {
        console.error('Error sending test email:', error);
        const errMsg =
          (error as any)?.response?.data?.message ||
          TOAST_MESSAGES.TEST_EMAIL_ERROR.description;
        notification.error(TOAST_MESSAGES.TEST_EMAIL_ERROR.title, errMsg);
      } finally {
        setTestLoading(false);
      }
    },
    [selectedTemplate]
  );

  const handleEditTemplate = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsFormOpen(true);
  }, []);

  const handleCreateTemplate = useCallback(() => {
    setSelectedTemplate(null);
    setIsFormOpen(true);
  }, []);

  const handleTestEmail = useCallback((template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsTestOpen(true);
  }, []);

  const handleDeleteClick = useCallback((template: EmailTemplate) => {
    setTemplateToDelete(template);
    setIsDeleteOpen(true);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchTemplates();
    fetchStats();
    fetchTemplateTypes();
  }, [fetchTemplates, fetchStats, fetchTemplateTypes]);

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('');
    setTypeFilter('');
    setIsDefaultFilter('');
    setCurrentPage(TEMPLATE_LIST_DEFAULTS.INITIAL_PAGE);
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [currentPage, limit, statusFilter, typeFilter, isDefaultFilter, fetchTemplates]);

  useEffect(() => {
    fetchStats();
    fetchTemplateTypes();
  }, [fetchStats, fetchTemplateTypes]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(TEMPLATE_LIST_DEFAULTS.INITIAL_PAGE);
      fetchTemplates();
    }, TEMPLATE_LIST_DEFAULTS.SEARCH_DELAY_MS);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, fetchTemplates]);

  const templateTypesWithFallback = useMemo(
    () => getTemplateTypesWithFallback(templateTypes as any),
    [templateTypes]
  );

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? 'dark' : 'light' }}>
      <S.MainContainer isDarkMode={isDarkMode}>
        <S.ContentWrapper>
          <S.SectionContainer>
            <S.HeaderContainer isDarkMode={isDarkMode}>
              <S.LeftSection>
                <S.IconButtonWrapper
                  isDarkMode={isDarkMode}
                  onClick={() => navigate(-1)}
                >
                  <ArrowLeftOutlined />
                </S.IconButtonWrapper>
                <S.MailIcon isDarkMode={isDarkMode}>
                  <MailOutlined />
                </S.MailIcon>
                <S.HeaderTitle isDarkMode={isDarkMode}>
                  Email Templates
                </S.HeaderTitle>
              </S.LeftSection>
              <S.RightSection>
                <Tooltip title="Refresh">
                  <S.IconButtonWrapper
                    isDarkMode={isDarkMode}
                    onClick={handleRefresh}
                  >
                    <ReloadOutlined />
                  </S.IconButtonWrapper>
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateTemplate}
                  size="large"
                >
                  Create Template
                </Button>
              </S.RightSection>
            </S.HeaderContainer>

            <StatsCards stats={stats} loading={statsLoading} />

            <TemplateFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              isDefaultFilter={isDefaultFilter}
              setIsDefaultFilter={setIsDefaultFilter}
              templateTypes={templateTypesWithFallback}
              onClearFilters={handleClearFilters}
            />

            <S.Section isDarkMode={isDarkMode}>
              {templates.length === 0 && !loading ? (
                <S.EmptyState isDarkMode={isDarkMode}>
                  <h3>No Templates Found</h3>
                  <p>
                    {searchTerm || statusFilter || typeFilter
                      ? 'No templates match your filters. Try adjusting your search criteria.'
                      : 'Get started by creating your first email template.'}
                  </p>
                </S.EmptyState>
              ) : (
                <TemplateTable
                  templates={templates}
                  loading={loading}
                  templateTypes={templateTypesWithFallback as any}
                  onEdit={handleEditTemplate}
                  onDuplicate={handleDuplicateTemplate}
                  onSetDefault={handleSetDefault}
                  onDelete={handleDeleteClick}
                  onTestEmail={handleTestEmail}
                />
              )}
            </S.Section>
          </S.SectionContainer>
        </S.ContentWrapper>
        <TemplateFormModal
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          template={selectedTemplate}
          onSave={handleSaveTemplate}
          templateTypes={templateTypesWithFallback}
          templateVariables={getTemplateVariables}
          loading={formLoading}
        />

        <TestEmailModal
          isOpen={isTestOpen}
          onClose={() => setIsTestOpen(false)}
          template={selectedTemplate as any}
          onSendTest={handleSendTestEmail}
          loading={testLoading}
        />

        <DeleteConfirmationDialog
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDeleteTemplate}
          templateName={templateToDelete?.name || ''}
          loading={deleteLoading}
        />
      </S.MainContainer>
    </ThemeProvider>
  );
};

export default EmailTemplateList;
